<template>
  <div ref="chartRef" class="line-chart"></div>
</template>

<script setup>
/* global defineProps */
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import * as echarts from 'echarts';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({ years: [], series: [] })
  },
  colors: {
    type: Array,
    default: () => []
  },
});

const chartRef = ref(null);
let chartInstance = null;

const defaultPalette = ['#66c2a5', '#fc8d62', '#e78ac3', '#a6d854', '#ffd92f', '#e5c494', '#b3b3b3'];
const getColor = (idx) => props.colors[idx] || defaultPalette[idx % defaultPalette.length];

const buildOption = () => {
  // 按 years + series 组装 ECharts option
  const years = props.data?.years || [];
  const seriesInput = props.data?.series || [];

  const series = seriesInput.map((s, idx) => ({
    name: s.name,
    type: 'line',
    data: s.values || [],
    showSymbol: true,
    lineStyle: { width: 2, color: getColor(idx) },
    itemStyle: { color: getColor(idx), borderColor: '#fff', borderWidth: 2 },
    emphasis: {
      focus: 'series',
      scale: true,
      itemStyle: { color: getColor(idx), borderColor: '#fff', borderWidth: 3 },
      symbol: 'circle',
      symbolSize: 10
    },
    smooth: props.smooth
  }));

  return {
    tooltip: {
      trigger: 'axis',
      // 自定义 tooltip 格式化函数
      // 这里对 params 进行排序处理，确保 tooltip 展示时按当前年份的数据值降序排列
      formatter(params) {
        // params 可能是数组（多系列），也可能是单对象
        const items = Array.isArray(params) ? [...params] : [params];
        // 按 value 从大到小排序，避免受图例顺序影响
        items.sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0));
        // 取当前横坐标标签
        const axisLabel = items[0]?.axisValueLabel ?? '';
        // 组装每一行的内容
        const lines = items.map((item) => `${item.marker} ${item.seriesName}：${item.value}`);
        // 返回最终 HTML
        return [axisLabel, ...lines].filter(Boolean).join('<br/>');
      }
    },
    legend: {
      bottom: 0,
    },
    grid: { left: 40, right: 20, top: 40, bottom: 50 },
    xAxis: {
      data: years,
      boundaryGap: false,
      splitLine: {
        show: true,
        lineStyle: { type: 'dashed', color: '#e5e7eb' }
      },
      axisPointer: {
        show: true,
        type: 'line',
        snap: true,
        lineStyle: { color: '#9ca3af', width: 1.5, type: 'solid' }
      }
    },
    yAxis: {
      type: 'value',
      splitNumber: 4,
      axisLine: { show: true, lineStyle: { color: '#9ca3af' } },
      axisTick: { show: false },
      axisLabel: { color: '#6b7280' },
      splitLine: { show: true, lineStyle: { type: 'dashed', color: '#e5e7eb' } }
    },
    series
  };
};

const renderChart = () => {
  // 首次渲染时初始化实例，后续仅 setOption
  if (!chartRef.value) return;
  if (!chartInstance) {
    chartInstance = echarts.init(chartRef.value);
  }
  chartInstance.setOption(buildOption(), true);
};

onMounted(() => {
  renderChart();
  window.addEventListener('resize', resizeChart);
});

const resizeChart = () => {
  // 容器尺寸变化时触发自适应
  if (chartInstance) chartInstance.resize();
};

watch(
  () => [props.data, props.colors],
  () => renderChart(),
  { deep: true }
);

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeChart);
  if (chartInstance) {
    chartInstance.dispose();
    chartInstance = null;
  }
});
</script>

<style scoped>
.line-chart {
  width: 100%;
  height: 360px;
}
</style>
